import React from 'react'
import { useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineFastMailIcon from 'LuxKit/Icons/line/LineFastMailIcon'
import LineCommentsAltIcon from 'LuxKit/Icons/line/LineCommentsAltIcon'

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const ContactContent = styled(PaneBody)`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const Root = styled(VerticalSpacer)`
  text-align: center;
`

const CONTACT_ITEMS = [
  { Icon: LinePhoneIcon, link: 'tel:1300820853' },
  { Icon: LineFastMailIcon, link: 'mailto:sales@luxuryescapes.com' },
  { Icon: LineCommentsAltIcon, link: '/support' },
] as const

function CruisesContact() {
  const intl = useIntl()

  const contactTranslations: Record<string, { title: string; text: string; subText: string }> = {
    'tel:1300820853': {
      title: intl.formatMessage({
        id: 'xNnpN62HaJ',
        defaultMessage: 'Call us',
        description: 'Title for phone contact option in cruise contact section',
      }),
      text: intl.formatMessage({
        id: 'xDzerbMCd8',
        defaultMessage: 'Found a better price? Please contact us, we’ll match it!',
        description: 'Description for phone contact option in cruise contact section',
      }),
      subText: intl.formatMessage({
        id: 'iuwv35pGLY',
        defaultMessage: 'Call 1300 820 853',
        description: 'Phone number link text in cruise contact section',
      }),
    },
    'mailto:sales@luxuryescapes.com': {
      title: intl.formatMessage({
        id: 'ba9XfwCoef',
        defaultMessage: 'Email us',
        description: 'Title for email contact option in cruise contact section',
      }),
      text: intl.formatMessage({
        id: 'YKSIKFMiaJ',
        defaultMessage: 'The Gift Card will be sent by email for you to print or forward to the lucky recipient.',
        description: 'Description for email contact option in cruise contact section',
      }),
      subText: intl.formatMessage({
        id: 'GY1Vh5/Xg/',
        defaultMessage: 'Send an email',
        description: 'Email link text in cruise contact section',
      }),
    },
    '/support': {
      title: intl.formatMessage({
        id: 'Dn2OnwxP2J',
        defaultMessage: 'Chat with us',
        description: 'Title for chat contact option in cruise contact section',
      }),
      text: intl.formatMessage({
        id: 'Txgo3vQgEm',
        defaultMessage: 'Need an answer right away? Chat live with a consultant now.',
        description: 'Description for chat contact option in cruise contact section',
      }),
      subText: intl.formatMessage({
        id: '7z3I27Cuts',
        defaultMessage: 'Chat now',
        description: 'Chat link text in cruise contact section',
      }),
    },
  }

  return <Pane>
    <PaneBody size="xl-y md">
      <LayoutContainer>
        <Heading variant="heading2" align="center" as="h2">
          {intl.formatMessage({
            id: 'iHbLe1QgGF',
            defaultMessage: 'Need help? Get in touch with us.',
            description: 'Heading for cruise contact section',
          })}
        </Heading>
        <ContactContent size="xl-y">
          {CONTACT_ITEMS.map(({ Icon, link }) => {
            const { title, text, subText } = contactTranslations[link]
            return (
              <SameHeightPaneSm key={link}>
                <PaneBody size="lg xl-y">
                  <Root gap={16}>
                    <Icon size={30} colour="primary" />
                    <div>
                      <BodyText variant="medium" weight="bold">
                        {title}
                      </BodyText>
                      <BodyText variant="small" as="p">
                        {text}
                      </BodyText>
                      <TextLink size="small" weight="bold" href={link}>
                        {subText}
                      </TextLink>
                    </div>
                  </Root>
                </PaneBody>
              </SameHeightPaneSm>
            )
          })}
        </ContactContent>
      </LayoutContainer>
    </PaneBody>
  </Pane>
}

export default CruisesContact
